import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let tries = 0;
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        await api.get(`/orders/${encodeURIComponent(orderId)}/verify`, { timeout: 10000 });
        const s = await api.get(`/orders/${encodeURIComponent(orderId)}/status`, { timeout: 10000 });
        if (cancelled) return;
        setStatus(s.data);
        setLoading(false);
        if (["awaiting_payment", "payment_pending"].includes(s.data.status) && tries < 5) {
          tries += 1; timer = setTimeout(poll, 2500);
        }
      } catch (e) {
        if (cancelled) return;
        try {
          const s = await api.get(`/orders/${encodeURIComponent(orderId)}/status`, { timeout: 10000 });
          if (cancelled) return;
          setStatus(s.data);
        } catch (statusError) {
          if (cancelled) return;
          setStatus({ error: statusError?.response?.status === 404 ? "not_found" : "unavailable" });
        }
        setLoading(false);
      }
    };
    if (orderId) poll(); else { setLoading(false); setStatus({ error: "missing_id" }); }
    return () => { cancelled = true; clearTimeout(timer); };
  }, [orderId]);

  const paid = status && ["paid", "queued", "calculating", "generating_report", "validating_pdf", "scheduled_for_delivery", "delivered"].includes(status.status);
  const pending = status && ["awaiting_payment", "payment_pending"].includes(status.status);
  const reportDelayed = status && ["generation_failed", "delivery_failed"].includes(status.status);
  const stopped = status && ["cancelled", "refund_pending", "refunded"].includes(status.status);
  const unknown = status && !status.error && !paid && !pending && !reportDelayed && !stopped;

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <header className="bg-ivory border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="font-serif text-2xl text-navy">Aetheria</span>
          </Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="bg-white rounded-2xl border border-border p-8 sm:p-12 max-w-lg w-full text-center" data-testid="payment-result">
          {loading && <><Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-4" /><p className="text-slate-500">Confirming your payment…</p></>}

          {!loading && paid && (
            <>
              <CheckCircle2 className="w-14 h-14 text-gold-hover mx-auto mb-5" data-testid="payment-success-icon" />
              <h1 className="font-serif text-4xl text-navy mb-3">{status.is_test_order ? "Test order confirmed!" : "Thank you for your order!"}</h1>
              <p className="text-slate-600 leading-relaxed">
                {status.is_test_order ? "No real payment was taken. " : "Your payment is confirmed. "}
                {status.status === "delivered"
                  ? "Your report is marked as delivered."
                  : status.confirmation_email_status === "failed"
                    ? "Your order is recorded, but email delivery needs attention. Please save the Order ID below."
                    : "Your report is being prepared for email delivery, usually within 6–7 hours."}
              </p>
              {status.confirmation_email_status === "failed" && status.status !== "delivered" && (
                <p className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 text-left" role="alert">
                  We couldn't send your confirmation email, and report delivery may also fail. Please don't place another order. Contact support with the Order ID below if you need your report.
                </p>
              )}
              <div className="mt-6 bg-paper rounded-xl p-5 text-left text-sm space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Order ID</span><span className="font-mono-accent text-navy">{status.order_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Report</span><span className="text-navy">{status.package_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="text-navy">₹{status.amount}</span></div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/track-order`} className="btn-primary" data-testid="go-track-order">Track your order</Link>
                <Link to="/" className="btn-ghost">Back home</Link>
              </div>
              <p className="mt-5 text-xs text-slate-400 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Delivery times are estimates.</p>
            </>
          )}

          {!loading && pending && (
            <>
              <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-5" />
              <h1 className="font-serif text-3xl text-navy mb-3">Payment not completed</h1>
              <p className="text-slate-600">Your payment has not been confirmed. If your bank shows a charge, check your order status before trying again.</p>
              <p className="mt-4 text-sm text-slate-500">Order ID: <strong className="font-mono-accent">{status.order_id}</strong></p>
              <Link to="/pricing" className="btn-gold mt-6 inline-flex">Try again</Link>
            </>
          )}

          {!loading && reportDelayed && (
            <>
              <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-5" />
              <h1 className="font-serif text-3xl text-navy mb-3">Your report is delayed</h1>
              <p className="text-slate-600">
                Your order was confirmed, but {status.status === "delivery_failed" ? "we couldn't deliver the email" : "report preparation encountered an issue"}.
                Please don't place another order. Contact support with your Order ID: <strong className="font-mono-accent">{status.order_id}</strong>.
              </p>
              <button type="button" onClick={() => window.location.reload()} className="btn-gold mt-6">Check again</button>
            </>
          )}

          {!loading && stopped && (
            <>
              <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-5" />
              <h1 className="font-serif text-3xl text-navy mb-3">Order update</h1>
              <p className="text-slate-600">Your order is {status.status.replace(/_/g, " ")}. Please contact support with Order ID <strong className="font-mono-accent">{status.order_id}</strong> if you have questions.</p>
            </>
          )}

          {!loading && status?.error && (
            <>
              <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-5" />
              <h1 className="font-serif text-3xl text-navy mb-3">{status.error === "unavailable" ? "We couldn't check your order" : "We couldn't find that order"}</h1>
              <p className="text-slate-600">{status.error === "unavailable" ? "Please try again in a moment. Do not place another order while we check its status." : "Please check your link or contact support."}</p>
              <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-6 inline-flex">Try again</button>
            </>
          )}

          {!loading && unknown && (
            <>
              <Clock className="w-14 h-14 text-amber-500 mx-auto mb-5" />
              <h1 className="font-serif text-3xl text-navy mb-3">Order status: {String(status.status).replace(/_/g, " ")}</h1>
              <p className="text-slate-600">Please save Order ID <strong className="font-mono-accent">{status.order_id}</strong> and contact support if this doesn't update.</p>
              <button type="button" onClick={() => window.location.reload()} className="btn-gold mt-6">Check again</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
